/**
 * Build a self-contained HTML document for the interactive Preview panel
 * (Artifacts-style): Tailwind CDN when needed, React+Babel for JSX,
 * no broken local imports — runs entirely inside the browser iframe.
 */

const TAILWIND_CDN = 'https://cdn.tailwindcss.com';
const REACT_CDN = 'https://unpkg.com/react@18.3.1/umd/react.development.js';
const REACT_DOM_CDN = 'https://unpkg.com/react-dom@18.3.1/umd/react-dom.development.js';
const BABEL_CDN = 'https://unpkg.com/@babel/standalone@7.26.9/babel.min.js';

function looksLikeFullDoc(html) {
  const s = String(html || '');
  return /<!doctype/i.test(s) || /<html[\s>]/i.test(s);
}

function usesTailwindClasses(source) {
  const s = String(source || '');
  if (/tailwind/i.test(s)) return true;
  // Common utility patterns (enough to catch Tailwind-authored UIs)
  return /\bclass(Name)?=["'][^"']*\b(flex|grid|hidden|block|inline|items-|justify-|gap-|p-\d|px-|py-|m-\d|mx-|my-|w-|h-|min-h-|max-w-|bg-|text-|font-|rounded|shadow|border|ring-|from-|to-|space-|divide-|opacity-|z-|overflow-|col-|row-|sm:|md:|lg:|xl:|2xl:)/i.test(s);
}

function looksLikeJsxOrReact(source) {
  const s = String(source || '');
  if (/\bfrom\s+['"]react['"]|\brequire\(['"]react['"]\)|\bReactDOM\b|\bcreateRoot\b|\bReact\./.test(s)) {
    return true;
  }
  // JSX tags: <Component or </Component
  if (/<\/?[A-Z][A-Za-z0-9]*[\s/>]/.test(s)) return true;
  if (/\bclassName=/.test(s) && /=>\s*\(?\s*</.test(s)) return true;
  return false;
}

/** Strip or neutralize broken relative / npm imports so the iframe doesn't throw. */
export function neutralizeBrokenImports(code) {
  let s = String(code || '');
  // ES module imports from relative or bare package paths → comment out
  s = s.replace(
    /^\s*import\s+[\s\S]*?from\s+['"](\.\/|\.\.\/|[^./'"][^'"]*)['"]\s*;?\s*$/gm,
    (line) => `/* preview: stripped import (self-contained) */ // ${line.trim()}`
  );
  s = s.replace(
    /^\s*import\s+['"](\.\/|\.\.\/|[^./'"][^'"]*)['"]\s*;?\s*$/gm,
    (line) => `/* preview: stripped import (self-contained) */ // ${line.trim()}`
  );
  s = s.replace(
    /require\s*\(\s*['"](\.\/|\.\.\/)[^'"]+['"]\s*\)/g,
    '(/* preview: stripped require */ null)'
  );
  return s;
}

function hasScriptSrc(doc, needle) {
  return String(doc || '').includes(needle);
}

function injectHeadTags(doc, tagsHtml) {
  const d = String(doc || '');
  if (d.includes('</head>')) {
    return d.replace('</head>', `${tagsHtml}\n</head>`);
  }
  if (d.includes('<head>')) {
    return d.replace('<head>', `<head>\n${tagsHtml}`);
  }
  if (d.includes('<html')) {
    return d.replace(/<html[^>]*>/i, (m) => `${m}\n<head>\n${tagsHtml}\n</head>`);
  }
  return `<!DOCTYPE html><html><head>${tagsHtml}</head><body>${d}</body></html>`;
}

function ensureRootMount(htmlBody) {
  const body = String(htmlBody || '').trim();
  if (!body) return '<div id="root"></div>';
  if (/id=["']root["']/.test(body) || /id=["']app["']/.test(body)) return body;
  return `<div id="root">${body}</div>`;
}

/**
 * @param {{ html?: string, css?: string, js?: string, fullDoc?: string, title?: string }} parts
 * @returns {string} complete HTML document for iframe write
 */
export function ensureInteractivePreviewDoc(parts = {}) {
  const title = parts.title || 'Preview panel';
  let fullDoc = String(parts.fullDoc || '').trim();
  let html = String(parts.html || '');
  let css = String(parts.css || '');
  let js = neutralizeBrokenImports(parts.js || '');

  if (!fullDoc && looksLikeFullDoc(html)) {
    fullDoc = html;
    html = '';
  }

  const combinedForDetect = `${fullDoc}\n${html}\n${css}\n${js}`;
  const needTailwind = usesTailwindClasses(combinedForDetect);
  const needReact = looksLikeJsxOrReact(combinedForDetect);

  if (!fullDoc) {
    const bodyHtml = needReact ? ensureRootMount(html) : (html || '<div id="app"></div>');
    const scriptType = needReact ? 'text/babel' : 'text/javascript';
    const headBits = [
      '<meta charset="UTF-8" />',
      '<meta name="viewport" content="width=device-width, initial-scale=1.0" />',
      `<title>${title}</title>`,
    ];
    if (needTailwind) {
      headBits.push(`<script src="${TAILWIND_CDN}"></script>`);
    }
    if (needReact) {
      headBits.push(`<script crossorigin src="${REACT_CDN}"></script>`);
      headBits.push(`<script crossorigin src="${REACT_DOM_CDN}"></script>`);
      headBits.push(`<script src="${BABEL_CDN}"></script>`);
    }
    headBits.push(`<style>\n${css}\n</style>`);

    // Default React mount if JSX/React code didn't include one
    let appJs = js;
    if (needReact && appJs && !/createRoot|ReactDOM\.render|render\s*\(/.test(appJs)) {
      appJs = `${appJs}\n\n;(() => {\n  const mount = document.getElementById('root') || document.getElementById('app');\n  if (!mount || typeof React === 'undefined' || typeof ReactDOM === 'undefined') return;\n  const App = typeof window.App !== 'undefined' ? window.App : null;\n  if (!App) return;\n  const root = ReactDOM.createRoot ? ReactDOM.createRoot(mount) : null;\n  if (root) root.render(React.createElement(App));\n  else ReactDOM.render(React.createElement(App), mount);\n})();\n`;
    }

    fullDoc = `<!DOCTYPE html>
<html lang="en">
<head>
${headBits.join('\n')}
</head>
<body>
${bodyHtml}
<script type="${scriptType}">
${appJs}
</script>
</body>
</html>`;
  } else {
    // Existing full document — inject CDNs if missing
    let doc = neutralizeBrokenImports(fullDoc);
    // Also neutralize import statements inside inline scripts roughly
    if (needTailwind && !hasScriptSrc(doc, 'cdn.tailwindcss.com') && !/tailwindcss/i.test(doc)) {
      doc = injectHeadTags(doc, `<script src="${TAILWIND_CDN}"></script>`);
    }
    if (needReact) {
      let inject = '';
      if (!hasScriptSrc(doc, 'react') && !/unpkg.com\/react@|cdnjs.*react/i.test(doc)) {
        inject += `<script crossorigin src="${REACT_CDN}"></script>\n`;
        inject += `<script crossorigin src="${REACT_DOM_CDN}"></script>\n`;
      }
      if (!hasScriptSrc(doc, 'babel.min') && !/@babel\/standalone/i.test(doc)) {
        inject += `<script src="${BABEL_CDN}"></script>\n`;
      }
      if (inject) doc = injectHeadTags(doc, inject);
      // Promote classic scripts that look like JSX to text/babel
      if (looksLikeJsxOrReact(doc) && !/type=["']text\/babel["']/.test(doc)) {
        doc = doc.replace(
          /<script(?![^>]*\bsrc=)(?![^>]*type=)([^>]*)>/gi,
          '<script type="text/babel"$1>'
        );
      }
    }
    // Append separate css/js if provided alongside a full doc
    if (css && !/<style[\s>]/i.test(doc)) {
      doc = injectHeadTags(doc, `<style>\n${css}\n</style>`);
    } else if (css) {
      doc = doc.includes('</head>')
        ? doc.replace('</head>', `<style>\n${css}\n</style>\n</head>`)
        : `${doc}\n<style>${css}</style>`;
    }
    if (js) {
      const scriptType = needReact ? 'text/babel' : 'text/javascript';
      const marker = `<script type="${scriptType}">\n${js}\n</script>\n`;
      if (doc.includes('</body>')) doc = doc.replace('</body>', `${marker}</body>`);
      else doc += marker;
    }
    fullDoc = doc;
  }

  return fullDoc;
}

export default ensureInteractivePreviewDoc;
